import { useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { Flex } from '@servicetitan/anvil2';
import { useFormContext } from 'react-hook-form';
import {
    Checkbox,
    Fieldset,
    Radio,
    RadioGroup,
    TextField,
    StateSelectField,
} from '@servicetitan/anvil2-react-hook-form';

/** Helper to build field names with prefix */
const fieldName = (prefix: string, field: string) => [prefix, field].join('.');

interface SpouseFormFieldsProps {
    /** Type of applicant */
    applicantType: 'primary' | 'co-applicant';
    /** Current state abbreviation (e.g., 'WI', 'MO') - watched by caller */
    state: string | undefined;
    /** Context where the fields are being rendered */
    context?: 'mailing-address' | 'job-details';
}

/**
 * SpouseFormFields - Centralized component for rendering marriage/spouse information fields
 *
 * The component automatically re-renders when the state prop changes (caller should watch the state field and pass it in).
 *
 * Provider's hasDetailedMarriageFields determines field type:
 * - true: Detailed fields with spouse name and address (GreenSky)
 * - false: Simple marital status with contact info (ServiceFinance)
 */
export const SpouseFormFields = ({
    applicantType,
    state,
    context = 'mailing-address',
}: SpouseFormFieldsProps) => {
    const providerConfig = useProviderConfig();
    const { statesWithSpouseInformation = [], hasDetailedMarriageFields = false } = providerConfig;

    const basePrefix = applicantType === 'primary' ? 'primaryApplicant' : 'coApplicant';
    const fieldPrefix = context === 'job-details' ? `${basePrefix}.jobDetails` : basePrefix;

    if (!state || !statesWithSpouseInformation.includes(state)) {
        return null;
    }

    if (hasDetailedMarriageFields) {
        return <SpouseAddressFields fieldPrefix={fieldPrefix} applicantType={applicantType} />;
    }

    return <MaritalStatusFields fieldPrefix={fieldPrefix} />;
};

interface InternalComponentProps {
    fieldPrefix: string;
    applicantType: 'primary' | 'co-applicant';
}

/**
 * Reusable marital status radio group
 */
const MaritalStatusRadioGroup = ({
    legend,
    fieldPrefix,
    fieldPath,
    gridColumn,
}: {
    legend: string;
    fieldPrefix: string;
    fieldPath: string;
    gridColumn?: string;
}) => {
    const name = fieldName(fieldPrefix, fieldPath);
    return (
        <RadioGroup legend={legend} name={name} required gridColumn={gridColumn}>
            <Radio name={name} value={false} label="Single" type="boolean" />
            <Radio name={name} value label="Married" type="boolean" />
        </RadioGroup>
    );
};

/**
 * Internal component: Detailed marriage fields with spouse name and address
 * Used when provider has hasDetailedMarriageFields = true or in job-details context
 */
const SpouseAddressFields = ({ fieldPrefix, applicantType }: InternalComponentProps) => {
    const { watch } = useFormContext();
    const isMarried = watch(fieldName(fieldPrefix, 'spouseInfo.isMarried')) as boolean;
    const isAddressSameAsApplicant = watch(
        fieldName(fieldPrefix, 'spouseInfo.isAddressSameAsApplicant'),
    ) as boolean;

    const applicantLabel = applicantType === 'primary' ? 'applicant' : 'co-applicant';

    return (
        <Fieldset>
            <MaritalStatusRadioGroup
                legend={`Is the ${applicantLabel} married?`}
                fieldPrefix={fieldPrefix}
                fieldPath="spouseInfo.isMarried"
                gridColumn="1/3"
            />

            {isMarried && (
                <>
                    <TextField
                        label="Spouse First Name"
                        name={fieldName(fieldPrefix, 'spouseInfo.firstName')}
                        required
                    />
                    <TextField
                        label="Spouse Last Name"
                        name={fieldName(fieldPrefix, 'spouseInfo.lastName')}
                        required
                    />
                    <Checkbox
                        name={fieldName(fieldPrefix, 'spouseInfo.isAddressSameAsApplicant')}
                        label={`Same as ${applicantLabel}'s address`}
                        gridColumn="1/3"
                    />
                    {!isAddressSameAsApplicant && (
                        <>
                            <TextField
                                label="Address Line 1"
                                name={fieldName(fieldPrefix, 'spouseInfo.address.street')}
                                required
                            />
                            <TextField
                                label="Address Line 2"
                                name={fieldName(fieldPrefix, 'spouseInfo.address.address2')}
                            />
                            <Flex
                                gap="4"
                                direction="column"
                                sm={{ direction: 'row' }}
                                style={{ width: '100%', gridColumn: '1 / -1' }}
                            >
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <TextField
                                        label="City"
                                        name={fieldName(fieldPrefix, 'spouseInfo.address.city')}
                                        required
                                    />
                                </div>
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <StateSelectField
                                        label="State"
                                        name={fieldName(fieldPrefix, 'spouseInfo.address.state')}
                                        style={{ width: '100%' }}
                                        required
                                    />
                                </div>
                                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                                    <TextField
                                        label="Zip Code"
                                        name={fieldName(fieldPrefix, 'spouseInfo.address.zip')}
                                        required
                                        style={{ width: '100%' }}
                                        inputMode="numeric"
                                    />
                                </div>
                            </Flex>
                        </>
                    )}
                </>
            )}
        </Fieldset>
    );
};

/**
 * Internal component: Simple marital status fields with spouse name and contact information
 * Used when provider has hasDetailedMarriageFields = false (e.g. ServiceFinance)
 */
const MaritalStatusFields = ({ fieldPrefix }: { fieldPrefix: string }) => {
    const { watch } = useFormContext();
    const maritalStatus = watch(fieldName(fieldPrefix, 'isMarried')) as boolean;

    return (
        <Fieldset>
            <MaritalStatusRadioGroup
                legend="Marital Status"
                fieldPrefix={fieldPrefix}
                fieldPath="isMarried"
            />
            {maritalStatus && (
                <Flex direction="column" gap="4">
                    <TextField
                        label="Spouse First Name"
                        name={fieldName(fieldPrefix, 'spouseFirstName')}
                        required
                    />
                    <TextField
                        label="Spouse Last Name"
                        name={fieldName(fieldPrefix, 'spouseLastName')}
                        required
                    />
                    <TextField
                        name={fieldName(fieldPrefix, 'spouseContact')}
                        label="Spouse Contact Information"
                        description="Enter your spouse's email address or phone number"
                        required
                    />
                </Flex>
            )}
        </Fieldset>
    );
};
